/**
 * The SOS alarm sound (slice 85, PLAN §8) — a framework-free singleton so the
 * banner, the app shell and any test drive the SAME one controller. React hooks
 * wrap it (`useSosAudio`); the audio itself lives here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BROWSER AUTOPLAY IS THE WHOLE PROBLEM (PLAN §13 risk).
 * ─────────────────────────────────────────────────────────────────────────────
 * A page cannot make noise until the user has interacted with it — every modern
 * browser suspends a fresh `AudioContext` until a gesture resumes it. An SOS can
 * fire on a tab the fleet manager has not yet touched, so the alarm model is:
 *
 *   - `arm()` runs INSIDE a user gesture (any click/keydown on the app, captured
 *     app-wide, AND the banner's own "Enable sound" button). It creates/resumes
 *     the context; a resumed context stays usable for the session.
 *   - If an SOS is active and the context is armed, `start()` loops the tone.
 *   - If it is NOT armed, the banner shows a one-tap "Enable sound" control
 *     (visual alarm meanwhile). Tapping it both arms AND starts, in the gesture.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SOUND (PLAN §8 — "generate a short alarm tone yourself, no downloads").
 * ─────────────────────────────────────────────────────────────────────────────
 * The canonical tone is `public/sounds/sos-alarm.wav`, self-synthesised by
 * `scripts/gen-sos-alarm.mjs` (an urgent 880/988 Hz warble that loops seamlessly).
 * We fetch + decode it once and loop the buffer. If that fetch/decode fails (a
 * cold metered connection, the asset missing), we FALL BACK to synthesising the
 * same two-tone warble live from oscillators — so the alarm is never silent for
 * lack of a file. Either way there is zero network cost until an SOS actually
 * fires: nothing here runs on the login path.
 *
 * jsdom (tests) has no `AudioContext`; every method below no-ops safely there, so
 * component tests can mount the banner without faking Web Audio.
 */

type Listener = () => void;

const ASSET_URL = '/sounds/sos-alarm.wav';
const TONES = [880, 988] as const;
const CELL_SEC = 0.18;
const PEAK_GAIN = 0.5;

interface AudioCtor {
  new (): AudioContext;
}

function getAudioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

class SosAlarm {
  private ctx: AudioContext | null = null;
  private buffer: AudioBuffer | null = null;
  private bufferTried = false;
  private bufferSource: AudioBufferSourceNode | null = null;
  private oscTimer: ReturnType<typeof setInterval> | null = null;
  private oscNodes: { osc: OscillatorNode; gain: GainNode } | null = null;
  private playing = false;
  private armedState = false;
  private wantPlaying = false;
  private listeners = new Set<Listener>();

  /** Subscribe to armed-state / playing changes (the banner re-renders its
   *  "Enable sound" affordance off this). Returns an unsubscribe. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  isArmed(): boolean {
    return this.armedState;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  /**
   * Arm (or confirm) audio — MUST be called from within a user gesture. Creates
   * the context lazily and resumes it. If an SOS was waiting (`wantPlaying`), it
   * starts the moment arming succeeds. Safe to call repeatedly.
   */
  arm(): void {
    const Ctor = getAudioCtor();
    if (!Ctor) return;
    if (!this.ctx) {
      try {
        this.ctx = new Ctor();
      } catch {
        return;
      }
      // Kick off the asset decode now that we have a context — before it is
      // needed, so the first alarm is not waiting on a fetch.
      void this.loadBuffer();
    }
    const ctx = this.ctx;
    const finish = () => {
      const armed = ctx.state === 'running';
      if (armed !== this.armedState) {
        this.armedState = armed;
        this.emit();
      }
      if (armed && this.wantPlaying && !this.playing) this.beginPlayback();
    };
    if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
      ctx.resume().then(finish, finish);
    } else {
      finish();
    }
  }

  private async loadBuffer(): Promise<void> {
    if (this.buffer || this.bufferTried || !this.ctx) return;
    this.bufferTried = true;
    try {
      const res = await fetch(ASSET_URL);
      if (!res.ok) return;
      const bytes = await res.arrayBuffer();
      this.buffer = await this.ctx.decodeAudioData(bytes);
    } catch {
      // Leave `buffer` null — playback falls back to live oscillator synthesis.
      this.buffer = null;
    }
  }

  /**
   * Request the alarm. If armed, it loops immediately; otherwise it is remembered
   * (`wantPlaying`) and begins the instant `arm()` succeeds — so a "Enable sound"
   * tap during an active SOS both arms and sounds in the one gesture.
   */
  start(): void {
    this.wantPlaying = true;
    if (!this.ctx || this.ctx.state !== 'running') {
      // Not armed yet. A context may already exist (armed earlier then the alarm
      // retriggered) — try to resume without a gesture; browsers allow resume on
      // a context the user has already unlocked once this session.
      if (this.ctx) this.arm();
      return;
    }
    if (!this.playing) this.beginPlayback();
  }

  /** Stop the alarm (acknowledged, or no SOS active). */
  stop(): void {
    this.wantPlaying = false;
    this.teardownNodes();
    if (this.playing) {
      this.playing = false;
      this.emit();
    }
  }

  private beginPlayback(): void {
    if (!this.ctx || this.playing) return;
    this.teardownNodes();
    if (this.buffer) {
      const src = this.ctx.createBufferSource();
      src.buffer = this.buffer;
      src.loop = true;
      const gain = this.ctx.createGain();
      gain.gain.value = PEAK_GAIN;
      src.connect(gain).connect(this.ctx.destination);
      src.start();
      this.bufferSource = src;
    } else {
      this.beginOscillator();
      // Keep trying to get the richer asset for next time.
      void this.loadBuffer();
    }
    this.playing = true;
    this.emit();
  }

  /** Fallback: synthesise the same two-tone warble from an oscillator, swapping
   *  frequency on a timer. Used when the wav asset could not be decoded. */
  private beginOscillator(): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = TONES[0];
    gain.gain.value = PEAK_GAIN * 0.5; // a square wave is harsher — pull it back
    osc.connect(gain).connect(this.ctx.destination);
    osc.start();
    this.oscNodes = { osc, gain };
    let hi = false;
    this.oscTimer = setInterval(() => {
      if (!this.ctx || !this.oscNodes) return;
      hi = !hi;
      this.oscNodes.osc.frequency.setValueAtTime(hi ? TONES[1] : TONES[0], this.ctx.currentTime);
    }, CELL_SEC * 1000);
  }

  private teardownNodes(): void {
    if (this.bufferSource) {
      try {
        this.bufferSource.stop();
      } catch {
        /* already stopped */
      }
      try {
        this.bufferSource.disconnect();
      } catch {
        /* no-op */
      }
      this.bufferSource = null;
    }
    if (this.oscTimer != null) {
      clearInterval(this.oscTimer);
      this.oscTimer = null;
    }
    if (this.oscNodes) {
      try {
        this.oscNodes.osc.stop();
        this.oscNodes.osc.disconnect();
        this.oscNodes.gain.disconnect();
      } catch {
        /* no-op */
      }
      this.oscNodes = null;
    }
  }
}

/** The ONE alarm for the app. */
export const sosAlarm = new SosAlarm();
